class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockScroll();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  lockScroll() {
    const requestedScrollPosition =
      typeof window.DLMCartDrawerOpenScrollY === 'number' && Number.isFinite(window.DLMCartDrawerOpenScrollY)
        ? Math.max(0, window.DLMCartDrawerOpenScrollY)
        : null;

    if (requestedScrollPosition !== null) {
      window.scrollTo(0, requestedScrollPosition);
      this.scrollPosition = requestedScrollPosition;
      window.DLMCartDrawerOpenScrollY = null;
    } else {
      this.scrollPosition = window.scrollY;
    }

    document.documentElement.style.setProperty('--scroll-lock-top', `-${this.scrollPosition}px`);
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');

    // iOS Safari: block touchmove on overlay to prevent background scroll
    this._preventTouchMove = (e) => {
      const drawerContent = this.querySelector('cart-drawer-items');
      const drawerFooter = this.querySelector('.drawer__footer');
      // Allow scrolling inside the drawer content and footer, block everywhere else
      if (drawerContent && drawerContent.contains(e.target)) return;
      if (drawerFooter && drawerFooter.contains(e.target)) return;
      e.preventDefault();
    };
    this.addEventListener('touchmove', this._preventTouchMove, { passive: false });
  }

  unlockScroll() {
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    document.documentElement.style.removeProperty('--scroll-lock-top');

    // Remove touchmove blocker
    if (this._preventTouchMove) {
      this.removeEventListener('touchmove', this._preventTouchMove);
      this._preventTouchMove = null;
    }

    window.scrollTo(0, this.scrollPosition || 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.ensureExpressCheckoutButtons();

    setTimeout(() => {
      this.resetDrawerScroll();
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // The re-rendered drawer carries fresh <shopify-accelerated-checkout-cart> markup, which
  // initialises itself once its custom element is defined. If the page was first rendered
  // with an empty cart and Shopify's wallet script is not loaded yet, ask Shopify to load it.
  ensureExpressCheckoutButtons() {
    try {
      if (!this.querySelector('shopify-accelerated-checkout-cart')) return;
      if (window.customElements && window.customElements.get('shopify-accelerated-checkout-cart')) return;
      const paymentButton = window.Shopify && window.Shopify.PaymentButton;
      if (paymentButton && typeof paymentButton.init === 'function') paymentButton.init();
    } catch (error) {
      console.error(error);
    }
  }

  resetDrawerScroll() {
    [this.querySelector('.drawer__inner'), this.querySelector('cart-drawer-items')].forEach((element) => {
      if (!element) return;
      element.scrollTop = 0;
      element.scrollLeft = 0;
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
